import { ApplicationRef, EmbeddedViewRef, inject, Injectable, signal, TemplateRef } from '@angular/core';
import { Subject, Observable } from 'rxjs';

import { LoggingService } from '../logging/logging.service';

@Injectable({
  providedIn: 'root'
})
export class HTMLBackgroundService {
  private readonly applicationRef = inject(ApplicationRef);
  private readonly loggingService = inject(LoggingService);

  public readonly isEditingLabels = signal<boolean>(false);

  private contentDocument: Document | null = null;
  private currentContentRefs: Array<EmbeddedViewRef<HTMLElement>> = [];
  private contentCleared$ = new Subject<void>();

  /**
   * Inject template content into all [nrc-content] placeholders in the registered content document.
   *
   * @param templateRef The template to render in the placeholders.
   */
  public showContentInPlaceholders(templateRef: TemplateRef<HTMLElement>): void {
    if (!this.contentDocument) {
      this.loggingService.warn('HTMLBackgroundService: No content document registered. Cannot show content in placeholders.');

      return;
    }

    // Clear any existing content first
    this.clearContent();

    // Find all [nrc-content] elements
    const placeholders = Array.from(this.contentDocument.querySelectorAll<HTMLElement>('[nrc-content]'));

    if (placeholders.length === 0) {
      this.loggingService.warn('HTMLBackgroundService: No [nrc-content] placeholders found in the content document.');

      return;
    }

    // Create and inject content into each placeholder
    for (const placeholder of placeholders) {
      // Create an embedded view directly from the template
      const viewRef = templateRef.createEmbeddedView({} as HTMLElement);

      // Attach to application ref for change detection
      this.applicationRef.attachView(viewRef);
      const rootNodes = viewRef.rootNodes;

      placeholder.innerHTML = '';

      for (const node of rootNodes) {
        placeholder.appendChild(node);
      }

      // Store reference for cleanup
      this.currentContentRefs.push(viewRef);
    }
  }

  /**
   * Clear all content from placeholders and destroy view references.
   */
  public clearContent(): void {
    // Destroy all view references
    for (const viewRef of this.currentContentRefs) {
      this.applicationRef.detachView(viewRef);

      viewRef.destroy();
    }

    this.currentContentRefs = [];

    this.contentCleared$.next();
  }

  /**
   * Observable that emits when content is cleared.
   *
   * @returns Observable that emits when content is cleared.
   */
  public watchContentCleared(): Observable<void> {
    return this.contentCleared$.asObservable();
  }

  /**
   * Toggles the current label edit mode.
   */
  public toggleLabelEditMode(): void {
    this.isEditingLabels.update((isEditingLabels) => !isEditingLabels);
  }

  /**
   * Update the current value of the label editing variable.
   *
   * @param isEditingLabels
   */
  public setLabelEditMode(isEditingLabels: boolean): void {
    this.isEditingLabels.set(isEditingLabels);
  }

  /**
   * Register the HTML background's content document where placeholders are located.
   *
   * @param document The content document containing [nrc-content] elements.
   */
  public registerContentDocument(document: Document): void {
    this.contentDocument = document;
  }

  /**
   * Unregister the current content document.
   */
  public unregisterContentDocument(): void {
    this.clearContent();

    this.contentDocument = null;
  }
}
